import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allArticles, categories } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const articles = useMemo(
    () => allArticles.filter((a) => a.category === slug),
    [slug]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，该分类不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = articles.slice(0, 10).map((article, index) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    url: `https://www.dapiguzuowen.com/article/${article.slug}`,
    position: index + 1,
  }));

  const collectionPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory.name} - 打屁股作文`,
    description: currentCategory.description,
    url: `https://www.dapiguzuowen.com/category/${currentCategory.slug}`,
    hasPart: articles.map((a) => ({
      "@type": "Article",
      headline: a.title,
      url: `https://www.dapiguzuowen.com/article/${a.slug}`,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageJsonLd)}</script>
      {articleJsonLd.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
      <main className="pt-16 bg-background-50">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-10 md:py-14">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  共 {currentCategory.count} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
              {currentCategory.description}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-8 md:py-12">
          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => navigate(`/category/${cat.slug}`)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? "bg-primary-500 text-background-50 shadow-sm"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/60"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-600">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <div className="w-full h-full bg-background-200 flex items-center justify-center">
                      <i className="ri-article-line text-4xl text-foreground-300"></i>
                    </div>
                  </div>
                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-4 line-clamp-2 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-secondary-100 text-secondary-800 rounded-md text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}